import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ContactPage() {
  const [activeTab, setActiveTab] = useState<'contact' | 'careers'>('contact');
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [careersSubmitted, setCareersSubmitted] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [careersLoading, setCareersLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    document.title = 'Contact DMV-Xpress Services Las Vegas | Careers — Licensed Nevada DMV Document Prep';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Contact DMV-Xpress Services in Las Vegas, NV for vehicle registration, title transfers, license plates & notary. Strict no-solicitation policy enforced. Careers available — no follow-up calls.');
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || ''}/contact`);

    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.setAttribute('data-page-schema', 'contact');
    schema.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      '@id': `${import.meta.env.VITE_SITE_URL || ''}/contact#webpage`,
      url: `${import.meta.env.VITE_SITE_URL || ''}/contact`,
      name: 'Contact DMV-Xpress Services Las Vegas',
      description: 'Contact DMV-Xpress Services in Las Vegas, NV for vehicle registration, title transfers, license plates & notary services.',
      isPartOf: { '@type': 'WebSite', url: import.meta.env.VITE_SITE_URL || '' },
      mainEntity: {
        '@type': 'LocalBusiness',
        '@id': `${import.meta.env.VITE_SITE_URL || ''}/#business`,
        name: 'DMV-Xpress Services',
        telephone: '+17028028696',
        email: 'info@dmv-xpress.com',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '4525 S Sandhill Rd',
          addressLocality: 'Las Vegas',
          addressRegion: 'NV',
          postalCode: '89121',
          addressCountry: 'US',
        },
        openingHoursSpecification: [
          { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '17:00' },
          { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '10:00', closes: '15:00' },
        ],
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+17028028696',
          contactType: 'customer service',
          areaServed: 'US-NV',
          availableLanguage: 'English',
        },
      },
    });
    document.head.appendChild(schema);
    return () => {
      const existing = document.head.querySelector('script[data-page-schema="contact"]');
      if (existing) document.head.removeChild(existing);
    };
  }, []);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setContactLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d7ddh7mm3fsfopf23qk0', { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: data.toString() });
      setContactSubmitted(true);
    } catch { setContactSubmitted(true); }
    finally { setContactLoading(false); }
  };

  const handleCareersSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCareersLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d7ddh7mm3fsfopf23qkg', { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: data.toString() });
      setCareersSubmitted(true);
    } catch { setCareersSubmitted(true); }
    finally { setCareersLoading(false); }
  };

  const inputCls = "w-full px-4 py-3 rounded-lg border text-sm text-gray-800 focus:outline-none transition-colors bg-white";
  const inputStyle = { borderColor: '#d1d5db' };
  const inputFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => { e.currentTarget.style.borderColor = '#003366'; };
  const inputBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => { e.currentTarget.style.borderColor = '#d1d5db'; };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Source Sans Pro', 'Segoe UI', sans-serif" }}>
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: 320 }}>
        <img
          src="https://public.readdy.ai/ai/img_res/f0386f5ab03fbf6402c13b6e87c48323.jpg"
          alt="DMV-Xpress Services Contact — Las Vegas Nevada"
          className="w-full h-full object-cover absolute inset-0"
          style={{ minHeight: 320 }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(0,40,85,0.92) 0%, rgba(0,40,85,0.75) 55%, rgba(0,40,85,0.4) 100%)' }}></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="inline-flex items-center gap-2 text-white text-xs font-bold px-4 py-2 rounded mb-5 uppercase tracking-widest" style={{ backgroundColor: '#E87722' }}>
            <i className="ri-shield-check-fill"></i>Licensed Third-Party Service — NRS240A
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white leading-tight mb-4">
            Contact <span style={{ color: '#E87722' }}>&amp; Careers</span>
          </h1>
          <p className="text-white/75 text-lg max-w-xl mb-6">
            Reach out for legitimate service inquiries only. Unsolicited sales and marketing are strictly prohibited.
          </p>
          {/* Tab Switcher */}
          <div className="inline-flex bg-white/10 border border-white/20 rounded-full p-1 gap-1">
            <button onClick={() => setActiveTab('contact')}
              className="px-7 py-2.5 rounded-full font-bold text-sm transition-all whitespace-nowrap"
              style={{ backgroundColor: activeTab === 'contact' ? '#E87722' : 'transparent', color: '#fff' }}>
              <i className="ri-mail-send-line mr-2"></i>Contact Us
            </button>
            <button onClick={() => setActiveTab('careers')}
              className="px-7 py-2.5 rounded-full font-bold text-sm transition-all whitespace-nowrap"
              style={{ backgroundColor: activeTab === 'careers' ? '#E87722' : 'transparent', color: '#fff' }}>
              <i className="ri-briefcase-line mr-2"></i>Careers
            </button>
          </div>
        </div>
      </section>

      {/* ── DISCLAIMER ── */}
      <div className="w-full py-3 border-b" style={{ backgroundColor: '#FFF8F0', borderColor: '#fde8cc' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-center gap-2 text-center text-sm">
          <i className="ri-information-fill flex-shrink-0" style={{ color: '#E87722' }}></i>
          <p className="text-gray-700">
            <strong style={{ color: '#003366' }}>DMV-Xpress Services is a licensed third-party document preparation service (NRS240A).</strong>
            {' '}We are NOT the Nevada Department of Motor Vehicles.{' '}
            <a href="https://dmv.nv.gov" target="_blank" rel="noopener noreferrer nofollow" className="underline font-semibold" style={{ color: '#003366' }}>Visit dmv.nv.gov →</a>
          </p>
        </div>
      </div>

      {/* ── CONTACT SECTION ── */}
      {activeTab === 'contact' && (
        <section className="py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* No-Solicitation Banner */}
            <div className="mb-10 rounded-lg border-2 overflow-hidden" style={{ borderColor: '#dc2626', backgroundColor: '#fff5f5' }}>
              <div className="px-6 py-4 flex items-center gap-4" style={{ backgroundColor: '#dc2626' }}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}>
                  <i className="ri-forbid-2-fill text-white text-xl"></i>
                </div>
                <div>
                  <h2 className="text-white font-black text-lg uppercase tracking-wide">⛔ Strict No-Solicitation Policy</h2>
                  <p className="text-red-100 text-xs font-semibold mt-0.5">Legally enforced — read before proceeding</p>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-800 font-bold text-base mb-4">This contact form is <span className="underline">exclusively</span> for clients seeking DMV document preparation services. The following are strictly prohibited:</p>
                <div className="grid sm:grid-cols-2 gap-2 mb-5">
                  {['Sales pitches or product offers', 'Marketing or promotional messages', 'Lead generation or data collection', 'SEO, web design, or digital services', 'Cold calls, cold emails, or cold visits', 'Any form of unsolicited business outreach'].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 rounded border" style={{ borderColor: '#fecaca', backgroundColor: '#fff' }}>
                      <i className="ri-close-circle-fill flex-shrink-0" style={{ color: '#dc2626' }}></i>
                      <span className="text-gray-700 text-sm font-medium">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="rounded-lg p-4 border" style={{ borderColor: '#fecaca', backgroundColor: '#fee2e2' }}>
                  <p className="text-red-800 font-bold text-sm">Violations will result in permanent blocking, reporting to regulatory authorities, and potential legal action. <span className="underline">No exceptions.</span></p>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-10">
              {/* Contact Info */}
              <div className="space-y-5">
                <div className="rounded-lg border p-6" style={{ borderColor: '#e5e7eb' }}>
                  <h3 className="font-black text-gray-900 text-lg mb-5 pb-3" style={{ borderBottom: '3px solid #E87722', display: 'inline-block' }}>Contact Information</h3>
                  <div className="space-y-4 mt-4">
                    {[
                      { icon: 'ri-phone-fill', label: 'Phone', val: '702.802.8696', href: 'tel:7028028696' },
                      { icon: 'ri-mail-fill', label: 'Email', val: 'info@dmv-xpress.com', href: 'mailto:info@dmv-xpress.com' },
                      { icon: 'ri-map-pin-fill', label: 'Address', val: '4525 S Sandhill Rd\nLas Vegas, NV 89121', isText: true },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#EEF4FF' }}>
                          <i className={`${item.icon} text-base`} style={{ color: '#003366' }}></i>
                        </div>
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-0.5">{item.label}</p>
                          {item.isText
                            ? <p className="text-gray-800 font-semibold text-sm whitespace-pre-line">{item.val}</p>
                            : <a href={item.href} className="font-semibold text-sm hover:underline" style={{ color: '#003366' }}>{item.val}</a>}
                        </div>
                      </div>
                    ))}
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#EEF4FF' }}>
                        <i className="ri-time-fill text-base" style={{ color: '#003366' }}></i>
                      </div>
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">Business Hours</p>
                        <div className="space-y-1 text-sm">
                          {[['Mon – Fri', '9:00 AM – 5:00 PM'], ['Saturday', '10:00 AM – 3:00 PM'], ['Sunday', 'Closed']].map(([d, h], i) => (
                            <div key={i} className="flex justify-between gap-6">
                              <span className="text-gray-500">{d}:</span>
                              <span className="font-semibold" style={{ color: h === 'Closed' ? '#E87722' : '#003366' }}>{h}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 p-4 rounded-lg border" style={{ backgroundColor: '#FFF8F0', borderColor: '#fde8cc' }}>
                    <p className="font-bold text-sm mb-1 flex items-center gap-2" style={{ color: '#E87722' }}>
                      <i className="ri-calendar-check-fill"></i>By Appointment Only
                    </p>
                    <p className="text-gray-600 text-xs leading-relaxed">Call <a href="tel:7028028696" className="font-semibold" style={{ color: '#003366' }}>702.802.8696</a> before visiting. Same-day available when confirmed by phone.</p>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden border" style={{ borderColor: '#e5e7eb' }}>
                  <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3223.8948108432234!2d-115.06516!3d36.08398!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c8c0b5c1234567%3A0x1234567890abcdef!2s4525%20S%20Sandhill%20Rd%2C%20Las%20Vegas%2C%20NV!5e0!3m2!1sen!2sus!4v1234567890123"
                    width="100%" height="220" style={{ border: 0, display: 'block' }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                    title="DMV-Xpress Services — 4525 S Sandhill Rd Las Vegas NV"></iframe>
                  <div className="p-3 border-t" style={{ borderColor: '#e5e7eb', backgroundColor: '#f8f9fa' }}>
                    <p className="text-gray-500 text-xs">S Sandhill Rd between Flamingo &amp; Tropicana. Free parking.</p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="lg:col-span-2">
                <div className="rounded-lg border p-8" style={{ borderColor: '#e5e7eb' }}>
                  {contactSubmitted ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: '#EEF4FF' }}>
                        <i className="ri-check-double-line text-3xl" style={{ color: '#003366' }}></i>
                      </div>
                      <h3 className="text-xl font-black text-gray-900 mb-2">Message Received</h3>
                      <p className="text-gray-500 text-base max-w-md leading-relaxed mb-2">If your inquiry is legitimate and relevant to our services, a team member will contact you within 1–2 business days.</p>
                      <p className="text-gray-400 text-sm">For faster service, call <a href="tel:7028028696" className="font-semibold" style={{ color: '#003366' }}>702.802.8696</a>.</p>
                    </div>
                  ) : (
                    <>
                      <h3 className="text-xl font-black text-gray-900 mb-1 pb-3" style={{ borderBottom: '3px solid #E87722', display: 'inline-block' }}>Send a Message</h3>
                      <p className="text-gray-500 text-sm mt-4 mb-6">For legitimate service inquiries only. Unsolicited communications will be permanently blocked.</p>
                      <form data-readdy-form onSubmit={handleContactSubmit} className="space-y-4">
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Full Name *</label>
                            <input required name="fullName" className={inputCls} style={inputStyle} onFocus={inputFocus} onBlur={inputBlur} placeholder="Your full legal name" />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Phone Number *</label>
                            <input required name="phone" type="tel" className={inputCls} style={inputStyle} onFocus={inputFocus} onBlur={inputBlur} placeholder="(702) 000-0000" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Email Address *</label>
                          <input required name="email" type="email" className={inputCls} style={inputStyle} onFocus={inputFocus} onBlur={inputBlur} placeholder="your@email.com" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Service Needed *</label>
                          <select required name="serviceNeeded" className={inputCls} style={{ ...inputStyle, backgroundColor: '#fff' }} onFocus={inputFocus} onBlur={inputBlur}>
                            <option value="">Select the service you need...</option>
                            <option value="vehicle-registration">Vehicle Registration</option>
                            <option value="title-transfer">Title Transfer</option>
                            <option value="license-plates">License Plates</option>
                            <option value="duplicate-title">Duplicate Title (VP-119)</option>
                            <option value="temporary-permits">Temporary Permits (10-day / 30-day)</option>
                            <option value="notary-services">Notary Services</option>
                            <option value="general-inquiry">General Inquiry (Existing Client)</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                            Message * <span className="text-gray-400 normal-case font-normal">(max 500 characters)</span>
                          </label>
                          <textarea required name="message" rows={5} maxLength={500}
                            onChange={(e) => setCharCount(e.target.value.length)}
                            className="w-full px-4 py-3 rounded-lg border text-sm text-gray-800 focus:outline-none transition-colors resize-none"
                            style={inputStyle} onFocus={inputFocus} onBlur={inputBlur}
                            placeholder="Describe your DMV service need. Include vehicle year, make, model if applicable."></textarea>
                          <div className="flex justify-between mt-1">
                            <p className="text-gray-400 text-xs">Legitimate service inquiries only</p>
                            <p className={`text-xs ${charCount > 450 ? 'text-red-500' : 'text-gray-400'}`}>{charCount}/500</p>
                          </div>
                        </div>
                        <div className="rounded-lg border p-4" style={{ borderColor: '#e5e7eb', backgroundColor: '#f8f9fa' }}>
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input required type="checkbox" name="acknowledgment" value="agreed" className="mt-1 flex-shrink-0" />
                            <span className="text-gray-600 text-xs leading-relaxed">
                              I confirm this is a <strong className="text-gray-800">legitimate service inquiry</strong> related to DMV document preparation. I am NOT submitting a sales pitch, marketing message, or any form of unsolicited outreach.
                            </span>
                          </label>
                        </div>
                        <button type="submit" disabled={contactLoading}
                          className="w-full text-white font-bold py-3.5 rounded-lg text-sm transition-all whitespace-nowrap disabled:opacity-60"
                          style={{ backgroundColor: '#E87722' }}
                          onMouseEnter={(e) => { if (!contactLoading) (e.currentTarget as HTMLElement).style.backgroundColor = '#d06a18'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#E87722'; }}>
                          {contactLoading ? <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin"></i>Sending...</span>
                            : <span className="flex items-center justify-center gap-2"><i className="ri-send-plane-fill"></i>Submit Legitimate Inquiry</span>}
                        </button>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── CAREERS SECTION ── */}
      {activeTab === 'careers' && (
        <section className="py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Careers Policy Warning */}
            <div className="mb-10 rounded-lg border-2 overflow-hidden" style={{ borderColor: '#dc2626', backgroundColor: '#fff5f5' }}>
              <div className="px-6 py-4 flex items-center gap-4" style={{ backgroundColor: '#dc2626' }}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}>
                  <i className="ri-alarm-warning-fill text-white text-xl"></i>
                </div>
                <div>
                  <h2 className="text-white font-black text-lg uppercase tracking-wide">⚠️ Mandatory Employment Policy — Non-Negotiable</h2>
                  <p className="text-red-100 text-xs font-semibold mt-0.5">Failure to comply results in immediate permanent application cancellation</p>
                </div>
              </div>
              <div className="p-6">
                <div className="rounded-lg border-2 p-5 mb-5" style={{ borderColor: '#dc2626', backgroundColor: '#fee2e2' }}>
                  <p className="text-red-900 font-black text-xl mb-3 uppercase leading-tight">DO NOT CALL. DO NOT EMAIL. DO NOT VISIT.<br /><span className="text-red-700">TO FOLLOW UP ON YOUR APPLICATION.</span></p>
                  <p className="text-red-800 font-bold text-sm leading-relaxed">Any attempt to follow up on your application status will result in the <span className="underline">IMMEDIATE AND PERMANENT CANCELLATION</span> of your application. This decision is final and irrevocable.</p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="rounded-lg border p-4" style={{ borderColor: '#fecaca', backgroundColor: '#fff' }}>
                    <p className="font-black text-sm mb-3 uppercase tracking-wide" style={{ color: '#dc2626' }}>Strictly Prohibited:</p>
                    <ul className="space-y-2">
                      {['Calling to ask about application status', 'Emailing HR or any staff member', 'Visiting the office to inquire about employment', 'Submitting multiple applications for the same role', 'Having anyone contact us on your behalf'].map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <i className="ri-close-circle-fill flex-shrink-0 mt-0.5" style={{ color: '#dc2626' }}></i>
                          <span className="text-gray-700 text-sm font-semibold">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-lg border p-4" style={{ borderColor: '#bbf7d0', backgroundColor: '#f0fdf4' }}>
                    <p className="font-black text-sm mb-3 uppercase tracking-wide text-green-700">Our HR Process:</p>
                    <ul className="space-y-2">
                      {['HR reviews every submitted application', 'Qualified candidates will be contacted directly', 'Review timelines vary — patience is required', 'Only qualified applicants receive a response', 'If not contacted, your application was not selected'].map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill flex-shrink-0 mt-0.5 text-green-600"></i>
                          <span className="text-gray-700 text-sm font-semibold">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-10">
              <div className="space-y-5">
                <div className="rounded-lg border p-6" style={{ borderColor: '#e5e7eb' }}>
                  <h3 className="font-black text-gray-900 text-lg mb-5 pb-3" style={{ borderBottom: '3px solid #E87722', display: 'inline-block' }}>Why Join DMV-Xpress?</h3>
                  <div className="space-y-4 mt-4">
                    {[
                      { icon: 'ri-award-fill', title: 'Established Business', desc: '10+ years serving Las Vegas — stable, growing company' },
                      { icon: 'ri-team-fill', title: 'Professional Team', desc: 'Work alongside licensed, experienced DMV specialists' },
                      { icon: 'ri-map-pin-fill', title: 'Las Vegas Location', desc: 'Conveniently located at 4525 S Sandhill Rd' },
                      { icon: 'ri-time-fill', title: 'Consistent Hours', desc: 'Mon–Fri 9AM–5PM, Sat 10AM–3PM — no late nights' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#EEF4FF' }}>
                          <i className={`${item.icon} text-base`} style={{ color: '#003366' }}></i>
                        </div>
                        <div>
                          <p className="font-bold text-gray-900 text-sm">{item.title}</p>
                          <p className="text-gray-500 text-xs">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-lg border p-6" style={{ borderColor: '#e5e7eb' }}>
                  <h3 className="font-black text-gray-900 text-base mb-4">Open Positions</h3>
                  <div className="space-y-3">
                    {[
                      { title: 'DMV Document Specialist', type: 'Full-Time', dept: 'Operations' },
                      { title: 'Notary Public / Document Prep', type: 'Full-Time', dept: 'Legal Services' },
                      { title: 'Client Services Representative', type: 'Full-Time', dept: 'Customer Service' },
                      { title: 'Office Administrator', type: 'Part-Time', dept: 'Administration' },
                    ].map((pos, i) => (
                      <div key={i} className="rounded-lg border p-4 hover:border-orange-300 transition-colors" style={{ borderColor: '#e5e7eb' }}>
                        <p className="font-bold text-gray-900 text-sm">{pos.title}</p>
                        <div className="flex gap-2 mt-1.5">
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: '#E87722' }}>{pos.type}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full text-gray-500 border" style={{ borderColor: '#e5e7eb' }}>{pos.dept}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2">
                <div className="rounded-lg border p-8" style={{ borderColor: '#e5e7eb' }}>
                  {careersSubmitted ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: '#EEF4FF' }}>
                        <i className="ri-check-double-line text-3xl" style={{ color: '#003366' }}></i>
                      </div>
                      <h3 className="text-xl font-black text-gray-900 mb-2">Application Submitted</h3>
                      <p className="text-gray-500 text-base max-w-md leading-relaxed mb-4">Your application has been received and will be reviewed by HR. If you are qualified, you will be contacted directly.</p>
                      <div className="rounded-lg border p-4 max-w-md" style={{ borderColor: '#fecaca', backgroundColor: '#fff5f5' }}>
                        <p className="font-bold text-sm mb-1 flex items-center gap-2" style={{ color: '#dc2626' }}><i className="ri-alarm-warning-fill"></i>Final Reminder</p>
                        <p className="text-red-700 text-sm leading-relaxed">Do NOT call, email, or visit to follow up. Any follow-up contact will result in immediate and permanent application cancellation.</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <h3 className="text-xl font-black text-gray-900 mb-1 pb-3" style={{ borderBottom: '3px solid #E87722', display: 'inline-block' }}>Employment Application</h3>
                      <p className="text-gray-500 text-sm mt-4 mb-6">Complete all fields accurately. HR reviews all applications — qualified candidates will be contacted. <strong className="text-red-600">Do not follow up by phone or email.</strong></p>
                      <form data-readdy-form onSubmit={handleCareersSubmit} className="space-y-4">
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">First Name *</label>
                            <input required name="firstName" className={inputCls} style={inputStyle} onFocus={inputFocus} onBlur={inputBlur} placeholder="First name" />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Last Name *</label>
                            <input required name="lastName" className={inputCls} style={inputStyle} onFocus={inputFocus} onBlur={inputBlur} placeholder="Last name" />
                          </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Email Address *</label>
                            <input required name="email" type="email" className={inputCls} style={inputStyle} onFocus={inputFocus} onBlur={inputBlur} placeholder="your@email.com" />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Phone Number *</label>
                            <input required name="phone" type="tel" className={inputCls} style={inputStyle} onFocus={inputFocus} onBlur={inputBlur} placeholder="(702) 000-0000" />
                          </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Position Applying For *</label>
                            <select required name="position" className={inputCls} style={{ ...inputStyle, backgroundColor: '#fff' }} onFocus={inputFocus} onBlur={inputBlur}>
                              <option value="">Select a position...</option>
                              <option value="dmv-document-specialist">DMV Document Specialist</option>
                              <option value="notary-document-prep">Notary Public / Document Prep</option>
                              <option value="client-services-rep">Client Services Representative</option>
                              <option value="office-administrator">Office Administrator</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Employment Type *</label>
                            <select required name="employmentType" className={inputCls} style={{ ...inputStyle, backgroundColor: '#fff' }} onFocus={inputFocus} onBlur={inputBlur}>
                              <option value="">Select type...</option>
                              <option value="full-time">Full-Time</option>
                              <option value="part-time">Part-Time</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Years of Relevant Experience *</label>
                          <select required name="experience" className={inputCls} style={{ ...inputStyle, backgroundColor: '#fff' }} onFocus={inputFocus} onBlur={inputBlur}>
                            <option value="">Select experience level...</option>
                            <option value="0-1">Less than 1 year</option>
                            <option value="1-2">1–2 years</option>
                            <option value="2-5">2–5 years</option>
                            <option value="5-10">5–10 years</option>
                            <option value="10+">10+ years</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Cover Letter / Why You Are Qualified * <span className="text-gray-400 normal-case font-normal">(max 500 characters)</span></label>
                          <textarea required name="coverLetter" rows={5} maxLength={500}
                            className="w-full px-4 py-3 rounded-lg border text-sm text-gray-800 focus:outline-none transition-colors resize-none"
                            style={inputStyle} onFocus={inputFocus} onBlur={inputBlur}
                            placeholder="Briefly describe your relevant experience, skills, and why you are a strong candidate."></textarea>
                        </div>
                        <div className="rounded-lg border-2 p-4" style={{ borderColor: '#fecaca', backgroundColor: '#fff5f5' }}>
                          <p className="font-bold text-sm mb-3 flex items-center gap-2" style={{ color: '#dc2626' }}><i className="ri-alarm-warning-fill"></i>You MUST acknowledge the following:</p>
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input required type="checkbox" name="noFollowUpAcknowledgment" value="agreed" className="mt-1 flex-shrink-0" />
                            <span className="text-red-800 text-sm leading-relaxed font-semibold">I understand that <strong>calling, emailing, or visiting to follow up on my application will result in the immediate and permanent cancellation of my application.</strong> I will wait for HR to contact me if I am qualified.</span>
                          </label>
                        </div>
                        <button type="submit" disabled={careersLoading}
                          className="w-full text-white font-bold py-3.5 rounded-lg text-sm transition-all whitespace-nowrap disabled:opacity-60"
                          style={{ backgroundColor: '#E87722' }}
                          onMouseEnter={(e) => { if (!careersLoading) (e.currentTarget as HTMLElement).style.backgroundColor = '#d06a18'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#E87722'; }}>
                          {careersLoading ? <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin"></i>Submitting...</span>
                            : <span className="flex items-center justify-center gap-2"><i className="ri-send-plane-fill"></i>Submit Application</span>}
                        </button>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
